import { useMemo } from 'react';

import { Badge } from '../../../../../common/Atoms/Badge';
import { MultiSelect } from '../../../../../common/Atoms/MultiSelect';
import { Paragraph } from '../../../../../common/Atoms/Typography/Paragraph';
import { useContact } from '../../../../hooks/useContact';
import { useContactTags } from '../../../../hooks/useContactTags';
import { useGetSelectedOrganisation } from '../../../../hooks/useGetSelectedOrganisation';
import { useUpdateContactPersonTagsMutation } from '../../../../services/api/contactsApi/contact';

interface ContactPersonTagSelectorProps {
  personId: string;
  personTags: string[];
}

export function ContactPersonTagSelector({
  personId,
  personTags,
}: ContactPersonTagSelectorProps) {
  const { data: contact } = useContact();
  const currentOrg = useGetSelectedOrganisation();
  const { tags } = useContactTags();
  const [updateContactPersonTags, { isLoading }] = useUpdateContactPersonTagsMutation();

  const displayValue = useMemo(() => {
    if (!personTags.length) {
      return (
        <Badge
          color={ `gray` }
          className={ `max-w-fit mr-2` }
          size={ `xxs` }
        >
          { `No tags` }
        </Badge>
      );
    }

    if (personTags.length === 1) {
      return (
        <Badge
          color={ `gray` }
          className={ `max-w-fit mr-2` }
          size={ `xxs` }
        >
          { personTags[0] }
        </Badge>
      );
    }

    return (
      <span className={ `flex items-center` }>
        <Badge
          color={ `gray` }
          className={ `max-w-fit mr-2` }
          size={ `xxs` }
        >
          { personTags[0] }
        </Badge>
        <Paragraph
          variant={ `secondary` }
          size={ `xs` }
          className={ `italic` }
        >
          { `+ ${personTags.length - 1} more` }
        </Paragraph>
      </span>
    );
  }, [personTags]);

  if (!contact || !currentOrg?.id) {
    return null;
  }

  function onChange(newPersonTags: string[] | number[]) {
    const nextTags = newPersonTags.filter((tag): tag is string => typeof tag === `string`);

    if (nextTags.length !== newPersonTags.length) {
      return;
    }

    updateContactPersonTags({
      organisationId: currentOrg.id,
      contactId: contact.id,
      personId,
      tags: nextTags,
    });
  }

  return (
    <MultiSelect
      ariaLabel={ `Person tags` }
      size={ `sm` }
      onChange={ onChange }
      options={ tags.map(tag => ({ value: tag, label: tag })) }
      selected={ personTags }
      display={ displayValue }
      loading={ isLoading }
      listButtonClassname={ personTags.length ? `py-0.5` : undefined }
      containerClassname={ `min-h-0 mt-2` }
    />
  );
}
